/**
 * ATT срез С2: вложения чата на стороне КЛИЕНТА — шифрование, метаданные,
 * расшифровка.
 *
 * Модель (дизайн ATT §1, повторяет Matrix): файл НЕ едет внутри сообщения.
 * Клиент шифрует его случайным одноразовым ключом, кладёт ОДИН блоб на сервер,
 * а в E2E-сообщение вставляет только ключ и метаданные. Отсюда всё остальное:
 *
 * - блоб один на всех получателей — трафик и диск не умножаются на состав
 *   комнаты (копия шифротекста на каждое устройство — это про конверты, и
 *   класть туда 5 МБ нельзя ни при каких лимитах);
 * - сервер физически не может прочитать вложение: у него случайные байты;
 * - 🔴 потеря ключа = потеря файла НАВСЕГДА. Ключ живёт в переписке, переписка
 *   в браузере. Это ровно то, о чём предупреждает памятка участнику.
 *
 * 🔴 Почему проверка типов здесь, а не на сервере. Сервер нюхает магические
 * байты (SEC-4) и отвергает активный контент — но у шифротекста сигнатуры нет,
 * и проверить он не может В ПРИНЦИПЕ. Решение владельца 03.09: принимаем любые
 * файлы, а безопасность держится на том, КАК мы их показываем ([canRenderInline]):
 * картинка — только в <img> из blob:, SVG инлайном НИКОГДА (это исполняемый
 * документ в нашем origin), всё остальное — «Скачать».
 */

/** Кап одного файла — ОРИГИНАЛА. Шифротекст длиннее на тег GCM (16 байт). */
export const MAX_ATTACHMENT_BYTES = 25 * 1024 * 1024;

/** Зеркало серверного MSG_ATTACHMENTS_PER_MESSAGE_MAX (msg_models.py). На
 *  приёме это кап РЕНДЕРА: чужой клиент мог прислать в шифротексте тысячу
 *  метаданных, и без капа лента заказала бы тысячу скачиваний. */
export const MAX_ATTACHMENTS_PER_MESSAGE = 10;

/** Длина тега AES-GCM: на столько шифротекст длиннее исходника. */
const GCM_TAG_BYTES = 16;

/**
 * 🔴 Типы, которые МОЖНО показать прямо в ленте.
 *
 * Список — белый, а не чёрный: неизвестный тип обязан ехать в «Скачать», а не
 * пробовать себя в разметке. `image/svg+xml` здесь НЕТ намеренно: SVG — это
 * документ со скриптами, и показ его инлайном в нашем origin означал бы XSS с
 * правами страницы чата (та же дыра, ради которой в SEC-4 нюхают байты).
 */
const INLINE_IMAGE_MIME = new Set([
  'image/png', 'image/jpeg', 'image/gif', 'image/webp', 'image/bmp',
]);

/** Показывать ли вложение прямо в ленте (иначе — кнопка «Скачать»). */
export function canRenderInline(mime) {
  return INLINE_IMAGE_MIME.has(String(mime || '').toLowerCase());
}

/**
 * Видео по расширению — ТОЛЬКО когда тип отправитель не знал.
 *
 * `file.type` бывает пустым (File API это допускает), и тогда отправитель
 * кладёт `application/octet-stream` (encryptAttachment). Без подсказки по
 * имени такие ролики — и уже лежащие в истории — навсегда остались бы
 * «Скачать». Явный НЕвидеотип именем не переопределяем: `отчёт.html`,
 * переименованный в `.mp4`, в плеер не пойдёт.
 */
const VIDEO_MIME_BY_EXT = {
  mp4: 'video/mp4', m4v: 'video/mp4', mov: 'video/quicktime', webm: 'video/webm',
};

/**
 * Видео: тип, под которым вложение пробовать в `<video>`, или null.
 *
 * 🔴 Не белый список «только mp4»: айфон снимает камерой `.mov`
 * (`video/quicktime`), и такой список отправил бы ролики наших же айфонщиков в
 * «Скачать». Можно ли проиграть — решает браузер ПОЛУЧАТЕЛЯ ([playableVideoMime]),
 * а честный откат при неподходящем кодеке — ошибка `<video>` (attachmentsUi).
 * Безопасность та же, что у картинок: чужой файл попадает только в медиаэлемент
 * из blob:, скриптов там не бывает.
 */
export function videoMimeOf(meta) {
  const mime = String(meta?.mime || '').trim().toLowerCase();
  if (mime.startsWith('video/')) return mime;
  if (mime !== '' && mime !== 'application/octet-stream') return null;
  const ext = /\.([a-z0-9]+)$/.exec(String(meta?.name || '').toLowerCase())?.[1];
  return VIDEO_MIME_BY_EXT[ext] ?? null;
}

const canPlayCache = new Map();

/** Ответ браузера `canPlayType`: '' — нет, 'maybe'/'probably' — пробуем. */
function browserCanPlay(mime) {
  if (!canPlayCache.has(mime)) {
    let answer = '';
    try {
      answer = document.createElement('video').canPlayType(mime) || '';
    } catch {
      answer = '';
    }
    canPlayCache.set(mime, answer !== '');
  }
  return canPlayCache.get(mime);
}

/**
 * 🔴 Apple WebKit младше 16 — видео из blob: не показываем в ленте.
 *
 * Ревью ВИДЕО В-1 (P2). В WebKit iOS 15 регрессия: запрос к blob:-видео шёл
 * без заголовка Range, и плеер перечитывал весь файл — минуты до старта,
 * рывки и расход памяти (bugs.webkit.org 232076, 232195; хвост с Range у
 * blob — 238170, март 2022). По отзывам исправление доехало около iOS 15.4, но
 * в какой именно выпуск — Apple не называет. `canPlayType` этого не видит, а
 * зависание не обязано дать `error` — наш откат не сработал бы. Поэтому порог
 * с запасом: до 16 — «Скачать», как на бою до этого среза; хуже не станет.
 *
 * 🔴 Спрашиваем ДВИЖОК, а не строку UA. Ревью р.2 нашло обход разбора UA, и
 * он не единственный: браузеры на iPhone/iPad в режиме «как компьютер» пишут
 * `Macintosh … CriOS/…` или `Chrome/…` без версии iOS. Контейнерные запросы
 * появились в Safari 16.0 (iOS так же), и Apple WebKit младше 16 их не
 * поддерживает, как бы ни представлялся. Попутно «Скачать» получат Chrome < 105
 * и Firefox < 110, то есть поведение боя. Остаток риска: iOS 15.4+ с вручную
 * включённым экспериментальным флагом, где ошибку WebKit по отзывам уже чинили.
 */
export function blobVideoUnsafeHere(css = globalThis.CSS) {
  try {
    return !css.supports('container-type', 'inline-size');
  } catch {
    return true;
  }
}

/** Тип для `<video>`, если браузер получателя берётся его играть; иначе null. */
export function playableVideoMime(meta) {
  const mime = videoMimeOf(meta);
  if (!mime || blobVideoUnsafeHere()) return null;
  return browserCanPlay(mime) ? mime : null;
}

/** Только для тестов: ответы `canPlayType` кэшируются на всю вкладку. */
export function _resetCanPlayCacheForTests() {
  canPlayCache.clear();
}

/** Человеческий размер: 4.2 МБ, 812 КБ. Для подписи под вложением. */
export function humanSize(bytes) {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n < 0) return '';
  if (n < 1024) return `${n} Б`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} КБ`;
  return `${(n / (1024 * 1024)).toFixed(1)} МБ`;
}

const b64 = {
  from: (bytes) => {
    let s = '';
    const view = new Uint8Array(bytes);
    // Порциями: apply на 25 МБ разом кладёт стек (RangeError).
    for (let i = 0; i < view.length; i += 0x8000) {
      s += String.fromCharCode.apply(null, view.subarray(i, i + 0x8000));
    }
    return btoa(s);
  },
  to: (text) => {
    const bin = atob(String(text));
    const out = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i += 1) out[i] = bin.charCodeAt(i);
    return out;
  },
};

const hex = (bytes) => Array.from(new Uint8Array(bytes))
  .map((b) => b.toString(16).padStart(2, '0')).join('');

/** sha256 в hex — тем же представлением, что считает сервер. */
export async function sha256Hex(bytes, crypto = globalThis.crypto) {
  return hex(await crypto.subtle.digest('SHA-256', bytes));
}

/**
 * Метаданные вложения — то, что едет ВНУТРИ шифротекста сообщения.
 *
 * 🔴 `sha256` здесь — хэш ШИФРОТЕКСТА, а не файла: его же считает сервер, и
 * получатель сверяет им, что скачал именно тот блоб. Хэш открытого файла в
 * переписке не нужен, а появившись, стал бы отпечатком содержимого.
 */
export function attachmentMeta({
  attachmentId, key, iv, sha256, name, mime, size,
}) {
  return {
    id: attachmentId, key, iv, sha256, name, mime, size,
  };
}

/** Форма метаданных из чужого сообщения. Мусор — не вложение, а не «почти». */
export function validMeta(m) {
  return Boolean(m)
    && typeof m.id === 'string' && m.id !== ''
    && typeof m.key === 'string' && m.key !== ''
    && typeof m.iv === 'string' && m.iv !== ''
    && typeof m.sha256 === 'string' && m.sha256 !== ''
    && typeof m.name === 'string'
    && typeof m.mime === 'string'
    && Number.isFinite(m.size) && m.size >= 0;
}

/**
 * Зашифровать файл одноразовым ключом.
 *
 * @returns {{ciphertext: ArrayBuffer, meta: object}} meta БЕЗ `id` — он
 *   появится только после загрузки: сервер выдаёт его сам.
 */
export async function encryptFile(file, crypto = globalThis.crypto) {
  // 🔴 Кап сверяется по ШИФРОТЕКСТУ (ревью ATT С2 р.1, Medium): серверный
  // лимит режет то, что он видит, — байты шифротекста, а тот длиннее файла
  // на тег GCM. Проверка по оригиналу пропускала бы файл ровно в 25 МБ,
  // шифровала его и получала 413 в конце — тот самый путь, от которого
  // проверка и должна была уберечь.
  if (cipherSizeOf(file.size) > MAX_ATTACHMENT_BYTES) {
    const err = new Error('too_large');
    err.code = 'too_large';
    throw err;
  }
  const key = await crypto.subtle.generateKey(
    { name: 'AES-GCM', length: 256 }, true, ['encrypt', 'decrypt'],
  );
  // 96 бит — размер IV, для которого GCM и спроектирован. Ключ одноразовый,
  // поэтому вопрос повторного использования IV не стоит: он ровно один.
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const plain = await file.arrayBuffer();
  const ciphertext = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv }, key, plain,
  );
  const raw = await crypto.subtle.exportKey('raw', key);
  return {
    ciphertext,
    meta: {
      key: b64.from(raw),
      iv: b64.from(iv),
      sha256: await sha256Hex(ciphertext, crypto),
      name: file.name || 'файл',
      mime: file.type || 'application/octet-stream',
      size: file.size,
    },
  };
}

/**
 * Расшифровать скачанный блоб.
 *
 * 🔴 Сверка sha256 идёт ДО расшифровки и ОБЯЗАТЕЛЬНА. GCM и сам поймает
 * подмену, но по-своему: он бросит невнятную ошибку, неотличимую от «ключ не
 * тот». Явная сверка отделяет «сервер отдал не тот блоб» от «сообщение
 * пришло битым», а человеку это две разные новости.
 */
export async function decryptAttachment(ciphertext, meta, crypto = globalThis.crypto) {
  if (!validMeta(meta)) {
    const err = new Error('bad_meta');
    err.code = 'bad_meta';
    throw err;
  }
  const actual = await sha256Hex(ciphertext, crypto);
  if (actual !== meta.sha256) {
    const err = new Error('sha_mismatch');
    err.code = 'sha_mismatch';
    throw err;
  }
  const key = await crypto.subtle.importKey(
    'raw', b64.to(meta.key), { name: 'AES-GCM' }, false, ['decrypt'],
  );
  let plain;
  try {
    plain = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: b64.to(meta.iv) }, key, ciphertext,
    );
  } catch {
    const err = new Error('undecryptable');
    err.code = 'undecryptable';
    throw err;
  }
  // Тип берём ИЗ МЕТАДАННЫХ, но показываем по ним же не всё (canRenderInline):
  // Blob с типом image/svg+xml сам по себе безопасен, опасен его показ.
  return new Blob([plain], { type: meta.mime });
}

/** Ожидаемый размер шифротекста — чтобы проверить кап ДО шифрования. */
export const cipherSizeOf = (size) => size + GCM_TAG_BYTES;
